import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { normalizeLLMMessage } from '../utils/llmAdapter';
import { adaptLLMChart } from '../utils/chartAdapter';

import { useMessages } from '../hooks/useMessages';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import GraphRenderer from './GraphRenderer';
import Citations from './Citations';
import styles from './InPageChat.module.scss';

interface SessionHistoryItem {
  id: string;
  userMessage: string;
  botResponse: string;
  generatedSql?: string;
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  generationTimeMs?: number;
  timestamp: string;
}

interface InPageChatProps {
  chatbot: any;
  preloadedMessages?: SessionHistoryItem[];
  preloadedSessionId?: string;
  onOpenChange?: (isOpen: boolean) => void;
}

export interface InPageChatHandle {
  startNewChat: () => void;
  focusInput: () => void;
}

const InPageChat = forwardRef<InPageChatHandle, InPageChatProps>(({
  chatbot,
  preloadedMessages = [],
  preloadedSessionId = '',
  onOpenChange
}, ref) => {
  const [inputText, setInputText] = useState('');
  const [sessionId, setSessionId] = useState<string>(preloadedSessionId);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [contextCollapsed, setContextCollapsed] = useState<boolean>(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [stopJustClicked, setStopJustClicked] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const SUGGESTED_PROMPTS = [
    'Summarize my documents',
    'What can you help me with?',
    'Search across my data'
  ];

  const { messages, loading, error, sendMessage, stopGeneration, clearMessages, removeLastTurnForRetry } = useMessages(
    preloadedMessages.map(item => [
      {
        id: `user-${item.id}`,
        role: 'user' as 'user',
        content: item.userMessage,
        timestamp: new Date(item.timestamp)
      },
      {
        id: `bot-${item.id}`,
        role: 'bot' as 'bot',
        content: item.botResponse,
        timestamp: new Date(item.timestamp)
      }
    ]).flat(),
    preloadedSessionId
  );

  // Function to generate a standard UUID v4
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  useEffect(() => {
    // Only generate a new session ID and clear messages if there are no preloaded messages
    if (preloadedMessages.length === 0) {
      const newSessionId = generateUUID();
      setSessionId(newSessionId);
      clearMessages();
    }
  }, [chatbot, clearMessages, preloadedMessages.length]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!loading && lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [loading, messages.length]);

  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    const checkScroll = () => {
      const threshold = 80;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      setShowScrollToBottom(!atBottom);
    };
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, [messages, loading]);

  const handleSendMessage = async () => {
    if (inputText.trim() === '') return;

    // Clear input immediately for better UX
    const messageToSend = inputText;
    setInputText('');

    try {
      await sendMessage(chatbot, messageToSend, sessionId);
      // Focus the textarea after sending the message only on desktop
      // Check if device is mobile/tablet
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // Only focus on non-mobile devices to match ChatGPT behavior
      if (!isMobile) {
        // Use setTimeout to ensure focus is set after any re-renders
        setTimeout(() => {
          if (textAreaRef.current) {
            textAreaRef.current.focus();
          }
        }, 0);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setInputText(messageToSend);
    }
  };

  const handleRetry = () => {
    if (!error) return;

    const lastUserMsg = [...messages].reverse().find((msg) => msg.role === 'user');
    if (!lastUserMsg) return;

    const contentToRetry = typeof lastUserMsg.content === 'string' ? lastUserMsg.content : '';
    if (!contentToRetry.trim()) return;

    removeLastTurnForRetry();
    sendMessage(chatbot, contentToRetry, sessionId).catch(() => {});
  };

  const handleStopClick = () => {
    stopGeneration();
    setStopJustClicked(true);
    window.setTimeout(() => setStopJustClicked(false), 400);
  };

  useImperativeHandle(ref, () => ({
    startNewChat: () => {
      clearMessages();
      const newSessionId = generateUUID();
      setSessionId(newSessionId);
      setInputText('');
      setCopiedMessageId(null);
      // Re-focus for desktop UX
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 0);
    },
    focusInput: () => {
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 0);
    }
  }), [clearMessages]);

  const renderMessageContent = (content: any) => {
    const { text, chart, citations } = normalizeLLMMessage(content);
    
    // Use adaptLLMChart to format the chart data
    const chartConfig = chart ? adaptLLMChart(chart) : null;

    return (
      <>
        {text && renderMarkdownContent(text)}
        {chartConfig && (
          <div className={styles.chartContainer}>
            <GraphRenderer {...chartConfig} />
          </div>
        )}
        {citations && citations.length > 0 && renderCitations(citations)}
      </>
    );
  };

  const renderMarkdownContent = (text: string) => {
    return (
      <div className={styles.markdownContent}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ node, ...props }) => <p className={styles.mdP} {...props} />,
            h1: ({ node, ...props }) => <h1 className={styles.mdH1} {...props}>{props.children || <span aria-hidden="true"> </span>}</h1>,
            h2: ({ node, ...props }) => <h2 className={styles.mdH2} {...props}>{props.children || <span aria-hidden="true"> </span>}</h2>,
            h3: ({ node, ...props }) => <h3 className={styles.mdH3} {...props}>{props.children || <span aria-hidden="true"> </span>}</h3>,
            h4: ({ node, ...props }) => <h4 className={styles.mdH4} {...props}>{props.children || <span aria-hidden="true"> </span>}</h4>,
            ul: ({ node, ...props }) => <ul className={styles.mdUl} {...props} />,
            ol: ({ node, ...props }) => <ol className={styles.mdOl} {...props} />,
            li: ({ node, ...props }) => <li className={styles.mdLi} {...props} />,
            code: ({ node, className, ...props }: any) => {
              const isBlock = className?.includes('language-');
              return isBlock ? (
                <code className={styles.mdCodeBlock} {...props} />
              ) : (
                <code className={styles.mdCodeInline} {...props} />
              );
            },
            pre: ({ node, ...props }) => <pre className={styles.mdPre} {...props} />,
            blockquote: ({ node, ...props }) => <blockquote className={styles.mdBlockquote} {...props} />,
            a: ({ node, ...props }) => <a className={styles.mdLink} {...props} target="_blank" rel="noopener noreferrer">{props.children || <span aria-hidden="true"> </span>}</a>,
            table: ({ node, ...props }) => <div className={styles.mdTableWrap}><table className={styles.mdTable} {...props} /></div>,
            thead: ({ node, ...props }) => <thead className={styles.mdThead} {...props} />,
            tbody: ({ node, ...props }) => <tbody className={styles.mdTbody} {...props} />,
            th: ({ node, ...props }) => <th className={styles.mdTh} {...props} />,
            td: ({ node, ...props }) => <td className={styles.mdTd} {...props} />,
            tr: ({ node, ...props }) => <tr className={styles.mdTr} {...props} />,
            hr: ({ node, ...props }) => <hr className={styles.mdHr} {...props} />
          }}
        >
          {text}
        </ReactMarkdown>
      </div>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for possible future use
  const _renderChartContent = (text: string) => {
    // Extract chart data from text
    const chartMatch = text.match(/\[CHART\]([\s\S]*?)\[\/CHART\]/);
    if (!chartMatch) return <div>{text}</div>;

    const chartData = chartMatch[1].trim();
    const lines = chartData.split('\n');

    // Parse chart properties
    let chartType: 'bar' | 'line' | 'pie' | 'area' = 'bar';
    let title = '';
    let xAxis = 'X-Axis';
    let yAxis = 'Y-Axis';
    const data: { label: string; value: number }[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('type:')) {
        const type = line.substring(5).trim() as 'bar' | 'line' | 'pie' | 'area';
        if (['bar', 'line', 'pie', 'area'].includes(type)) {
          chartType = type;
        }
      } else if (line.startsWith('title:')) {
        title = line.substring(6).trim();
      } else if (line.startsWith('xAxis:')) {
        xAxis = line.substring(6).trim();
      } else if (line.startsWith('yAxis:')) {
        yAxis = line.substring(6).trim();
      } else if (line.includes(':')) {
        const [label, valueStr] = line.split(':');
        if (label && valueStr) {
          data.push({
            label: label.trim(),
            value: parseFloat(valueStr.trim())
          });
        }
      }
    }

    // If no title was specified, use a default one
    if (!title) {
      title = 'Chart';
    }

    // Render with GraphRenderer for better visualization and download capability
    if (data.length > 0) {
      return (
        <div className={styles.chartContainer}>
          <GraphRenderer
            type={chartType}
            title={title}
            data={data}
            xAxis={xAxis}
            yAxis={yAxis}
          />
        </div>
      );
    }

    // Fallback to original bar chart rendering if no data parsed
    return (
      <div className={styles.chartContainer}>
        <div className={styles.chartTitle}>{title}</div>
        <div className={styles.barChart}>
          {data.map((bar, index) => {
            const maxValue = Math.max(...data.map(b => b.value));
            const percentage = (bar.value / maxValue) * 100;
            return (
              <div key={index} className={styles.barItem}>
                <div className={styles.barLabel}>{bar.label}</div>
                <div className={styles.barContainer}>
                  <div
                    className={styles.barFill}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className={styles.barValue}>{bar.value}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Keep the original bar content renderer for backward compatibility
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _renderBarContent = (text: string) => {
    // Extract bar data from text
    const barMatch = text.match(/\[BARS\]([\s\S]*?)\[\/BARS\]/);
    if (!barMatch) return <div>{text}</div>;

    const barData = barMatch[1].trim();
    const lines = barData.split('\n');

    // Parse title and data
    const title = lines[0];
    const barItems: { label: string, value: number }[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const [label, valueStr] = line.split(':');
      if (label && valueStr) {
        barItems.push({
          label: label.trim(),
          value: parseFloat(valueStr.trim())
        });
      }
    }

    return (
      <div className={styles.chartContainer}>
        <div className={styles.chartTitle}>{title}</div>
        <div className={styles.barChart}>
          {barItems.map((item, index) => {
            const maxValue = Math.max(...barItems.map(b => b.value));
            const percentage = (item.value / maxValue) * 100;
            return (
              <div key={index} className={styles.barItem}>
                <div className={styles.barLabel}>{item.label}</div>
                <div className={styles.barContainer}>
                  <div
                    className={styles.barFill}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className={styles.barValue}>{item.value}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

 const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Check if device is mobile/tablet
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if(isMobile) return;
      e.preventDefault();
      handleSendMessage().then(() => {
        // Only focus on non-mobile devices to match ChatGPT behavior
        if (!isMobile) {
          // Ensure focus returns to textarea after sending with Enter key
          setTimeout(() => {
            if (textAreaRef.current) {
              textAreaRef.current.focus();
            }
          }, 0);
        }
      });
    }
  };

  // Render generic structured data in a table format
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _renderGenericDataTable = (text: string) => {
    // Simple approach: split by lines and create a basic table
    const lines = text.split('\n');

    // Find lines with list items
    const listLines = lines.filter(line => line.trim().startsWith('- '));

    // Find summary/statistic lines
    const summaryLines = lines.filter(line =>
      ((line.includes(':') && !line.startsWith('- ')) || !!line.match(/Total .+: \d+/))
    );

    return (
      <div className={styles.markdownContent}>
        {listLines.length > 0 && (
          <div className={styles.dataTableContainer}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Information</th>
                </tr>
              </thead>
              <tbody>
                {listLines.map((line, index) => (
                  <tr key={index}>
                    <td>{line.trim().substring(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {summaryLines.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {summaryLines.map((line, index) => (
              <p key={index} style={{ margin: '5px 0' }}><strong>{line.trim()}</strong></p>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render citations in a collapsible format
  const renderCitations = (citations: any[]) => {
    return <Citations citations={citations} />;
  };

  const hasCitations = (content: any) => {
    const { citations } = normalizeLLMMessage(content);
    return !!(citations && citations.length);
  };

  const getCopyText = (content: any) => {
    const { text } = normalizeLLMMessage(content);
    if (typeof text === 'string' && text.trim().length > 0) return text;
    if (typeof content === 'string' && content.trim().length > 0) return content;
    try {
      return JSON.stringify(content, null, 2);
    } catch {
      return String(content ?? '');
    }
  };

  const handleCopy = async (msgId: string, content: any) => {
    const copyText = getCopyText(content);
    if (!copyText) return;
    try {
      await navigator.clipboard.writeText(copyText);
      setCopiedMessageId(msgId);
      setShowCopiedToast(true);
      window.setTimeout(() => setCopiedMessageId(prev => (prev === msgId ? null : prev)), 1200);
      window.setTimeout(() => setShowCopiedToast(false), 1600);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleSuggestedPrompt = (text: string) => {
    setInputText(text);
    textAreaRef.current?.focus();
  };

  const scrollToBottom = () => {
    chatContainerRef.current?.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: 'smooth' });
  };

  const CopyIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
  const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const formatTimestamp = (timestamp?: Date) => {
    if (!timestamp) return '';
    try {
      return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 150)}px`;
  };

  // startNewChat is exposed via useImperativeHandle (inline above); this const is for potential reuse
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startNewChat = () => {
    clearMessages();
    const newSessionId = generateUUID();
    setSessionId(newSessionId);
  };

  // Call the callback when component mounts (when chat is shown)
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(true);
    }
    
    // Cleanup function to call when component unmounts (when chat is hidden)
    return () => {
      if (onOpenChange) {
        onOpenChange(false);
      }
    };
  }, [onOpenChange]);

  const contextSources = useMemo(() => {
    const map = new Map<string, { sourceName: string; bestConfidence?: number }>();
    messages.forEach((msg) => {
      if (msg.role !== 'bot') return;
      const { citations } = normalizeLLMMessage(msg.content);
      if (!citations || !citations.length) return;
      citations.forEach((c: any) => {
        const existing = map.get(c.sourceId);
        const nextConfidence = typeof c.confidence === 'number' ? c.confidence : undefined;
        if (!existing) {
          map.set(c.sourceId, {
            sourceName: c.sourceName || c.sourceId,
            bestConfidence: nextConfidence
          });
        } else if (
          typeof nextConfidence === 'number' &&
          (typeof existing.bestConfidence !== 'number' || nextConfidence > existing.bestConfidence)
        ) {
          map.set(c.sourceId, {
            sourceName: existing.sourceName,
            bestConfidence: nextConfidence
          });
        }
      });
    });
    return Array.from(map.entries())
      .map(([sourceId, value]) => ({ sourceId, ...value }))
      .sort((a, b) => (b.bestConfidence ?? 0) - (a.bestConfidence ?? 0))
      .slice(0, 6);
  }, [messages]);

  return (
    <div className={styles.inPageChatWindow}>
      <div className={styles.chatMain}>
        {showCopiedToast && (
          <div className={styles.copiedToast} role="status" aria-live="polite">
            Copied to clipboard
          </div>
        )}
        <div
          className={styles.chatMessages}
          ref={chatContainerRef}
          role="log"
          aria-label="Chat messages"
          aria-live="polite"
        >
        {messages.length === 0 && !loading && (
          <div className={styles.suggestedPrompts}>
            <p className={styles.suggestedPromptsTitle}>Get started</p>
            <div className={styles.suggestedPromptsList}>
              {SUGGESTED_PROMPTS.map((prompt, i) => (
                <button
                  key={i}
                  type="button"
                  className={styles.suggestedPromptBtn}
                  onClick={() => handleSuggestedPrompt(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
            {/* <p className={styles.suggestedPromptsTip}>
              Ask questions, summarize docs, or search your data.
            </p> */}
          </div>
        )}
        {messages.map((msg, idx) => {
          const isFirstUser = msg.role === 'user' && messages.findIndex(m => m.role === 'user') === idx;
          const isFirstBot = msg.role === 'bot' && messages.findIndex(m => m.role === 'bot') === idx;
          const showSeparator = idx > 0 && idx % 4 === 0;
          const isLastMessage = idx === messages.length - 1;
          return (
            <React.Fragment key={msg.id}>
              {showSeparator && <div className={styles.messageSeparator} aria-hidden="true" />}
              <div
                ref={isLastMessage ? lastMessageRef : undefined}
                className={`${styles.message} ${styles[msg.role]} ${isFirstUser ? styles.firstUser : ''} ${isFirstBot ? styles.firstBot : ''}`}
              >
              <div className={styles.messageContent}>
                <div className={styles.messageHeader}>
                  <div className={styles.messageHeaderMain}>
                    {msg.role === 'bot' && hasCitations(msg.content) && (
                      <span className={styles.messageBadge}>Sources</span>
                    )}
                  </div>
                  <div className={styles.messageHeaderMeta}>
                    <div className={styles.messageActions}>
                      {msg.role === 'bot' && (
                        <button
                          type="button"
                          className={`${styles.messageActionBtn} ${copiedMessageId === msg.id ? styles.copied : ''}`}
                          onClick={() => handleCopy(msg.id, msg.content)}
                          title={copiedMessageId === msg.id ? 'Copied' : 'Copy'}
                          aria-label={copiedMessageId === msg.id ? 'Copied' : 'Copy'}
                        >
                          {copiedMessageId === msg.id ? <CheckIcon /> : <CopyIcon />}
                          {copiedMessageId === msg.id && (
                            <span className={styles.copyFeedbackText}>Copied</span>
                          )}
                        </button>
                      )}
                    </div>
                    {msg.timestamp && (
                      <span className={styles.messageTimestampHover} title={formatTimestamp(msg.timestamp)}>
                        {formatTimestamp(msg.timestamp)}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.messageBubble}>
                  {renderMessageContent(msg.content || '')}
                </div>
              </div>
            </div>
            </React.Fragment>
          );
        })}
        {loading && (
          <div className={`${styles.message} ${styles.bot}`}>
            <div className={styles.messageContent}>
              <div className={styles.messageBubble}>
                <div className={styles.loadingState}>
                  <span className={styles.loadingLabel}>Thinking</span>
                  <div className={styles.loadingDots}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <div className={styles.loadingSkeleton}>
                  <span className={styles.skeletonLine} />
                  <span className={styles.skeletonLine} />
                  <span className={styles.skeletonLineShort} />
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
        {error && (
          <div className={styles.sendErrorBar} role="alert">
            <span className={styles.sendErrorText}>{error}</span>
            <button
              type="button"
              className={styles.retryBtn}
              onClick={handleRetry}
              aria-label="Retry sending"
            >
              Retry
            </button>
          </div>
        )}
        <div className={styles.chatInputArea}>
          {showScrollToBottom && (messages.length > 0 || loading) && (
            <button
              type="button"
              className={styles.scrollToBottomBtn}
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </svg>
            </button>
          )}
          <div className={`${styles.chatInputWrapper} ${loading ? styles.chatInputWrapperLoading : ''}`}>
            <textarea
              ref={textAreaRef}
              className={styles.chatInput}
              placeholder="Message your assistant..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onInput={handleInput}
              onKeyPress={handleKeyPress}
              rows={1}
              disabled={loading}
              aria-label="Message input"
            />
            {loading ? (
              <button
                type="button"
                className={styles.stopBtn}
                onClick={handleStopClick}
                disabled={stopJustClicked}
                aria-label="Stop generating"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className={styles.sendBtn}
                onClick={handleSendMessage}
                disabled={!inputText.trim()}
                aria-label="Send message"
              >
                ↑
              </button>
            )}
          </div>
        </div>
      </div>
      <div className={styles.contextPanelWrap}>
        <button
          type="button"
          className={styles.contextToggle}
          onClick={() => setContextCollapsed(prev => !prev)}
          title={contextCollapsed ? 'Expand sources' : 'Collapse sources'}
        >
          {contextCollapsed ? '▶' : '▼'}
        </button>
        {!contextCollapsed && (
          <aside className={styles.contextPanel}>
            <div className={styles.contextHeader}>
              <div className={styles.contextTitleBlock}>
                <div className={styles.contextTitle}>Sources used</div>
                <div className={styles.contextSubtitle}>
                  Grounding documents & confidence from this session
                </div>
              </div>
              {contextSources.length > 0 && (
                <span className={styles.contextCount}>{contextSources.length}</span>
              )}
            </div>
            {contextSources.length > 0 ? (
              <div className={styles.contextList}>
                {contextSources.map((src) => (
                  <div key={src.sourceId} className={styles.contextItem}>
                    <div className={styles.contextName}>{src.sourceName}</div>
                    <div className={styles.contextMeta}>
                      <span className={styles.contextSourceId}>{src.sourceId}</span>
                      {typeof src.bestConfidence === 'number' && (
                        <span className={styles.contextConfidence}>
                          {(src.bestConfidence * 100).toFixed(0)}% confidence
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.contextEmpty}>
                <div className={styles.contextEmptyTitle}>No sources yet</div>
                <div className={styles.contextEmptySubtitle}>
                  Ask a question to see which documents the assistant relies on.
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
});

export default InPageChat;
